import { useEffect, useState } from "react";

/* The parts of the product that are not for everybody.
   ---------------------------------------------------------------------------
   A handful of things in here are built for the people building it: the
   content map, the butler, and the two demonstration estates on the
   multiverse's own band. They are real and they work, and none of them is
   something a customer opening the product should be shown - a rail with two
   entries nobody outside this building can use is a rail that has to be
   explained, and a band offering five estates when three of them are theirs
   is a question asked of the reader for no reason.

   So they are off, and there is one way to turn them on: a chord nobody
   presses by accident. Not a setting, because a setting is a thing you have
   to put somewhere and then explain; not a build flag, because the people who
   want these want them in the build everybody else is running. One chord, one
   flag, every one of them at once.

   The flag is kept where every other preference in this product is kept and
   is broadcast when it moves, because the things it hides are in two
   different trees - the rail is mounted beside the router and the band is
   inside a page - and neither is a parent of the other. */
export const BACKSTAGE_KEY = "appfactor.backstage";
/* ...and the chord. Four keys, one of which the browser already uses on its
   own, which is the point: nothing lands on this by accident. */
export const BACKSTAGE_CHORD = "Ctrl+Shift+Alt+F11";
const MOVED = "appfactor:backstage";

/* ...and the other switch, on the key beside it: draw from the cache alone.
   ---------------------------------------------------------------------------
   Every slice this product reads is already written to IndexedDB as it
   arrives, and read back out of it on a cold start while the network catches
   up. That is a fallback. This makes it the SOURCE: the estate is filled from
   what is on disk and nothing is asked of the server at all.

   It is here for the two cases where that is what you want - working on the
   drawing with no backend to hand, and looking at exactly the data somebody
   else was looking at when they reported something - and in both of them the
   important thing is that it be obvious. A page quietly showing yesterday's
   estate is a page that will have somebody chasing a bug that was fixed this
   morning, so the state says so on the band in the product's own orange. */
export const CACHED_KEY = "appfactor.cacheonly";
export const CACHED_CHORD = "Ctrl+Shift+Alt+F10";
const TURNED = "appfactor:cacheonly";

/** Whether a flag kept beside the rest of this product's settings is on. */
function flagAt(key) {
  try {
    return localStorage.getItem(key) === "true";
  } catch (e) {
    /* a private window has no storage, and the answer there is "no" */
    return false;
  }
}

/** Whether the parts that are not for everybody are being shown. */
export function isBackstage() {
  return flagAt(BACKSTAGE_KEY);
}

/** Whether the estate is being drawn from the cache rather than the server. */
export function isCached() {
  return flagAt(CACHED_KEY);
}

function setFlag(key, said, on) {
  try {
    localStorage.setItem(key, on ? "true" : "false");
  } catch (e) { /* private window */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(said, { detail: !!on }));
  }
  return !!on;
}

/** Show them, or stop showing them, and tell everything that is watching. */
export function setBackstage(on) {
  return setFlag(BACKSTAGE_KEY, MOVED, on);
}

/** Draw from the cache, or go back to asking the server. */
export function setCached(on) {
  return setFlag(CACHED_KEY, TURNED, on);
}

/* ...and the chord itself, armed ONCE however many things are watching.
   -------------------------------------------------------------------------
   Armed at the module rather than in the hook below, which is the whole of
   why it is here: a listener per watcher is a toggle per watcher, so the rail
   and the band would each flip the flag on one press and two watchers would
   leave it exactly where it started. */
let armed = false;
function arm() {
  if (armed || typeof window === "undefined") return;
  armed = true;
  window.addEventListener("keydown", (e) => {
    if (!e.ctrlKey || !e.shiftKey || !e.altKey) return;
    if (e.key === "F11") {
      /* ...and the browser's own fullscreen left alone, since it is the
         same key and the reader asked for this one */
      e.preventDefault();
      setBackstage(!isBackstage());
      return;
    }
    if (e.key === "F10") {
      e.preventDefault();
      setCached(!isCached());
    }
  });
}
arm();

/**
 * Whether to draw the parts that are not for everybody, kept current.
 *
 * Reads the flag on mount and follows it from then on, so the chord moves
 * the rail and the band in the same frame wherever either of them is.
 */
function useFlag(key, said, read) {
  const [on, setOn] = useState(read);
  useEffect(() => {
    arm();
    const moved = e => setOn(typeof e.detail === "boolean" ? e.detail : read());
    /* ...and the same flag changed in ANOTHER tab, which `storage` reports
       and the event above does not: two windows of this product open at once
       should not disagree about what is in the rail. */
    const elsewhere = (e) => { if (!e.key || e.key === key) setOn(read()); };
    window.addEventListener(said, moved);
    window.addEventListener("storage", elsewhere);
    return () => {
      window.removeEventListener(said, moved);
      window.removeEventListener("storage", elsewhere);
    };
  }, [key, said, read]);
  return on;
}

export function useBackstage() {
  return useFlag(BACKSTAGE_KEY, MOVED, isBackstage);
}

/** Whether the estate is being drawn from the cache, kept current. */
export function useCached() {
  return useFlag(CACHED_KEY, TURNED, isCached);
}

export default useBackstage;
